import { useEffect, useRef, useState } from 'react';
import {
  SERVER_RENDER_HISTORY_SAVE_LIMIT,
  isRenderResultAlreadyInHistory,
} from '../utils/photorealistic/renderHistoryDisplay';
import { downloadRenderImageAs } from '../utils/photorealistic/downloadRenderImage';

function getSaveItemTooltip({ item, serverRenderHistory, saveLimitReached, isSaving }) {
  if (isSaving) return 'Saving to render library…';
  if (isRenderResultAlreadyInHistory(item, serverRenderHistory)) {
    return 'Already saved to render library';
  }
  if (saveLimitReached) {
    return `Maximum ${SERVER_RENDER_HISTORY_SAVE_LIMIT} saved renders per image. Delete one from the Render Library to save another.`;
  }
  return 'Save to render library';
}

function sortRenderingJobsForDisplay(jobs = []) {
  const queued = [];
  const generating = [];
  for (const job of jobs) {
    if (String(job?.statusLabel ?? '').toLowerCase() === 'generating') {
      generating.push(job);
    } else {
      queued.push(job);
    }
  }
  const bySubmittedAsc = (a, b) =>
    Number(a?.submittedAt ?? 0) - Number(b?.submittedAt ?? 0);
  queued.sort(bySubmittedAsc);
  generating.sort(bySubmittedAsc);
  return [...queued, ...generating];
}

function RenderInProgressCell({ statusLabel = 'on queue' }) {
  return (
    <li className="min-w-0">
      <div
        className="relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50 shadow-sm"
        aria-label={`Rendering in progress: ${statusLabel}`}
        role="status"
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 backdrop-blur-[1px]">
          <svg
            className="h-6 w-6 animate-spin text-primary-300"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="mt-1.5 text-[10px] font-medium capitalize text-primary-300">
            {statusLabel}
          </span>
        </div>
      </div>
    </li>
  );
}

const RenderHistoryGrid = ({
  items = [],
  columns = 2,
  renderingJobs = [],
  maxVisibleItems = null,
  compareActive = false,
  compareSelectedIds = [],
  onCompareToggleItem,
  maxCompareSelection = 2,
  onSelectItem,
  onSaveItem,
  onDeleteItem,
  savingItemId = null,
  serverRenderHistory = [],
  saveLimitReached = false,
  deletingItemId = null,
  enableExport = false,
  className = '',
}) => {
  const [failedUrlIndexByItemId, setFailedUrlIndexByItemId] = useState({});
  const [exportMenuItemId, setExportMenuItemId] = useState(null);
  const [exportingItemId, setExportingItemId] = useState(null);
  const exportMenuRef = useRef(null);

  useEffect(() => {
    if (exportMenuItemId == null) return undefined;
    const handlePointerDown = (event) => {
      if (exportMenuRef.current?.contains(event.target)) return;
      setExportMenuItemId(null);
    };
    window.addEventListener('pointerdown', handlePointerDown);
    return () => window.removeEventListener('pointerdown', handlePointerDown);
  }, [exportMenuItemId]);

  useEffect(() => {
    setFailedUrlIndexByItemId({});
  }, [items]);

  const orderedRenderingJobs = sortRenderingJobsForDisplay(renderingJobs);
  const visibleRenderingJobs =
    maxVisibleItems != null
      ? orderedRenderingJobs.slice(0, maxVisibleItems)
      : orderedRenderingJobs;
  const remainingItemSlots =
    maxVisibleItems != null
      ? Math.max(0, maxVisibleItems - visibleRenderingJobs.length)
      : items.length;
  const visibleItems =
    maxVisibleItems != null ? items.slice(0, remainingItemSlots) : items;

  if (!visibleItems.length && visibleRenderingJobs.length === 0) return null;

  return (
    <ul
      className={`grid gap-1.5 ${className}`.trim()}
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {visibleRenderingJobs.map((job) => (
        <RenderInProgressCell
          key={job.jobId}
          statusLabel={job.statusLabel || 'on queue'}
        />
      ))}
      {visibleItems.map((item) => {
        const key = String(item?.id ?? item?.createdAt ?? item?.imageUrl);
        const itemIdKey = String(item?.id ?? key);
        const candidates = Array.isArray(item?.imageUrlCandidates)
          ? item.imageUrlCandidates.filter((url) => typeof url === 'string' && url.trim())
          : [item?.imageUrl, item?.lightboxImageUrl].filter(
              (url) => typeof url === 'string' && url.trim(),
            );
        const failedIndex = Number(failedUrlIndexByItemId[itemIdKey] ?? 0);
        const imageUrl = candidates[failedIndex] ?? null;
        const isDeleting = deletingItemId != null && Number(deletingItemId) === Number(item?.id);
        const isSaving = savingItemId != null && String(savingItemId) === String(item?.id);
        const alreadySaved = isRenderResultAlreadyInHistory(item, serverRenderHistory);
        const saveDisabled =
          isSaving ||
          isDeleting ||
          alreadySaved ||
          saveLimitReached ||
          (savingItemId != null && !isSaving) ||
          (deletingItemId != null && !isDeleting);
        const saveTooltip = getSaveItemTooltip({
          item,
          serverRenderHistory,
          saveLimitReached,
          isSaving,
        });
        const isCompareSelected = compareSelectedIds.some(
          (id) => String(id) === String(item?.id),
        );
        const compareSelectionFull =
          compareActive &&
          compareSelectedIds.length >= maxCompareSelection &&
          !isCompareSelected;
        const compareCheckboxDisabled = compareSelectionFull || !imageUrl;
        const isExporting = exportingItemId != null && String(exportingItemId) === String(item?.id);
        const exportMenuOpen =
          enableExport &&
          exportMenuItemId != null &&
          String(exportMenuItemId) === String(item?.id);
        const exportImageUrl = item?.lightboxImageUrl ?? imageUrl;
        const exportDisabled =
          isDeleting ||
          isExporting ||
          !exportImageUrl ||
          (deletingItemId != null && !isDeleting);

        const handleExport = async (format) => {
          if (!exportImageUrl || exportDisabled) return;
          setExportingItemId(item?.id ?? null);
          try {
            await downloadRenderImageAs(exportImageUrl, format, 'render');
            setExportMenuItemId(null);
          } catch (err) {
            console.error('Failed to export render:', err);
          } finally {
            setExportingItemId(null);
          }
        };

        return (
          <li key={key} className="min-w-0">
            <div
              className={`relative aspect-[4/3] w-full ${
                compareActive && isCompareSelected ? 'rounded-lg ring-2 ring-primary-300' : ''
              }`}
            >
              <button
                type="button"
                onClick={() => {
                  if (compareActive) {
                    if (!compareCheckboxDisabled) onCompareToggleItem?.(item);
                    return;
                  }
                  onSelectItem?.(item);
                }}
                disabled={isDeleting || (compareActive && compareCheckboxDisabled)}
                className={`group block h-full w-full overflow-hidden rounded-lg border bg-gray-50 shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                  compareActive && isCompareSelected
                    ? 'border-primary-300'
                    : 'border-gray-200 hover:border-primary-300'
                }`}
                title={
                  compareActive
                    ? isCompareSelected
                      ? 'Deselect render'
                      : compareSelectionFull
                        ? 'Select up to 2 renders'
                        : 'Select render to compare'
                    : 'View saved render'
                }
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt="Saved render"
                    className="h-full w-full object-cover"
                    loading="lazy"
                    draggable={false}
                    referrerPolicy="no-referrer"
                    onError={() => {
                      setFailedUrlIndexByItemId((prev) => ({
                        ...prev,
                        [itemIdKey]: failedIndex + 1,
                      }));
                    }}
                  />
                ) : (
                  <span className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">
                    No preview
                  </span>
                )}
              </button>

              {compareActive ? (
                <label
                  className={`absolute right-1 top-1 z-10 flex h-5 w-5 items-center justify-center rounded border bg-white/95 shadow-sm ${
                    compareCheckboxDisabled
                      ? 'cursor-not-allowed border-gray-200 opacity-50'
                      : 'cursor-pointer border-gray-200 hover:border-primary-300'
                  }`}
                  title={
                    isCompareSelected
                      ? 'Deselect render'
                      : compareSelectionFull
                        ? 'Select up to 2 renders'
                        : 'Select render to compare'
                  }
                >
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 accent-primary-300"
                    checked={isCompareSelected}
                    disabled={compareCheckboxDisabled}
                    onChange={(event) => {
                      event.stopPropagation();
                      if (!compareCheckboxDisabled) onCompareToggleItem?.(item);
                    }}
                    onClick={(event) => event.stopPropagation()}
                    aria-label={
                      isCompareSelected ? 'Deselect render for compare' : 'Select render for compare'
                    }
                  />
                </label>
              ) : null}

              {!compareActive && onSaveItem ? (
                <span
                  className="absolute right-7 top-1 z-10"
                  title={saveTooltip}
                >
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!saveDisabled) onSaveItem(item);
                    }}
                    disabled={saveDisabled}
                    className="flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-gray-600 shadow-sm transition-colors hover:border-primary-300 hover:bg-white hover:text-primary-300 disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label={saveTooltip}
                  >
                  {isSaving ? (
                    <svg
                      className="h-3 w-3 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="10"
                      height="10"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                      <polyline points="17 21 17 13 7 13 7 21" />
                      <polyline points="7 3 7 8 15 8" />
                    </svg>
                  )}
                  </button>
                </span>
              ) : null}

              {!compareActive && enableExport ? (
                <div
                  ref={exportMenuOpen ? exportMenuRef : null}
                  className="absolute right-7 top-1 z-10"
                >
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (exportDisabled) return;
                      setExportMenuItemId((currentId) =>
                        String(currentId) === String(item?.id) ? null : item?.id ?? null,
                      );
                    }}
                    disabled={exportDisabled}
                    className="flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-gray-600 shadow-sm transition-colors hover:border-primary-300 hover:bg-white hover:text-primary-300 disabled:cursor-not-allowed disabled:opacity-50"
                    title="Export render"
                    aria-label="Export render"
                    aria-expanded={exportMenuOpen}
                  >
                    {isExporting ? (
                      <svg
                        className="h-3 w-3 animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        aria-hidden
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    )}
                  </button>

                  {exportMenuOpen ? (
                    <div
                      className="absolute right-[-23px] top-6 min-w-[100px] rounded-lg border border-gray-200 bg-white px-2 py-1 shadow-lg"
                      onClick={(event) => event.stopPropagation()}
                      role="menu"
                      aria-label="Export render as"
                    >
                      <p className="mb-1.5 text-[10px] text-center font-semibold uppercase tracking-wide text-gray-500">
                        Export As
                      </p>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          role="menuitem"
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleExport('jpeg');
                          }}
                          disabled={isExporting}
                          className="flex-1 rounded border border-gray-200 px-2 py-1 text-[10px] font-medium text-gray-700 transition-colors hover:border-primary-300 hover:text-primary-300 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          JPEG
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleExport('png');
                          }}
                          disabled={isExporting}
                          className="flex-1 rounded border border-gray-200 px-2 py-1 text-[10px] font-medium text-gray-700 transition-colors hover:border-primary-300 hover:text-primary-300 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          PNG
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {!compareActive && onDeleteItem ? (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onDeleteItem(item);
                  }}
                  disabled={isDeleting || deletingItemId != null}
                  className="absolute right-1 top-1 z-10 flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-gray-600 shadow-sm transition-colors hover:border-red-300 hover:bg-white hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                  title="Delete render"
                  aria-label="Delete render"
                >
                  {isDeleting ? (
                    <svg
                      className="h-3 w-3 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="10"
                      height="10"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="M18 6L6 18" />
                      <path d="M6 6l12 12" />
                    </svg>
                  )}
                </button>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default RenderHistoryGrid;
